import {
  type EnvironmentId,
  type ClientOrchestrationEvent,
  type ClientOrchestrationShellSnapshot,
  type ClientOrchestrationShellStreamEvent,
  ThreadId,
  OLDER_MESSAGE_PAGE_SIZE,
  OLDER_ACTIVITY_PAGE_SIZE,
} from "@t3tools/contracts";
import {
  createWsRpcClient as createBaseWsRpcClient,
  fetchShellSnapshot,
  type WsRpcClient,
} from "@t3tools/client-runtime";
// Subpath, not the barrel: `@dovod/config`'s barrel re-exports `codex-config.ts`,
// which imports `node:os`. Vite externalizes that for the browser and its stub
// throws on the named import, so pulling one constant through the barrel took the
// whole client down at load with an error that named neither file.
import { SERVER_TOKEN_REFRESH_INTERVAL_SECONDS } from "@dovod/config/server-identity";

import { type QueryClient } from "@tanstack/react-query";
import { scopedThreadKey, scopeProjectRef, scopeThreadRef } from "@t3tools/client-runtime";

import {
  markPromotedDraftThreadByRef,
  markPromotedDraftThreadsByRef,
  useComposerDraftStore,
} from "~/composerDraftStore";
import { deriveOrchestrationBatchEffects } from "~/orchestrationEventEffects";
import { acquireServerToken, getPrimaryKnownEnvironment, issueWebSocketTicket } from "../primary";
import { attachServerToken } from "../primary/serverToken";
import { type PrimaryKnownEnvironment, resolvePrimaryEnvironmentHttpUrl } from "../primary/target";
import { primaryEnvironmentFetch } from "../../lib/runtime";
import { primaryEnvironmentRequestInit } from "../primary/requestInit";
import { createEnvironmentConnection, type EnvironmentConnection } from "./connection";
import {
  useStore,
  selectProjectsAcrossEnvironments,
  selectSidebarThreadSummaryByRef,
  selectThreadByRef,
  selectThreadsAcrossEnvironments,
  mapMessage,
  selectEnvironmentState,
} from "~/store";
import { useUiStateStore } from "~/uiStateStore";
import { getServerConfig } from "../../rpc/serverState";
import { WsTransport } from "~/rpc/wsTransport";
import { resolveServerConfigVersionMismatch } from "../../versionSkew";
import { deriveLogicalProjectKey, derivePhysicalProjectKey } from "../../logicalProject";

import { resetWsReconnectBackoff } from "~/rpc/wsConnectionState";
import { t } from "~/i18n";

type EnvironmentServiceState = {
  readonly queryClient: QueryClient;
  refCount: number;
  stop: () => void;
};

type ThreadDetailSubscriptionEntry = {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  unsubscribe: () => void;
  unsubscribeConnectionListener: (() => void) | null;
  refCount: number;
  viewCount: number;
  deferred: boolean;
  lastAccessedAt: number;
  evictionTimeoutId: ReturnType<typeof setTimeout> | null;
};

type ThreadDetailRetainPurpose = "view" | "prewarm";

const environmentConnections = new Map<EnvironmentId, EnvironmentConnection>();
const environmentConnectionListeners = new Set<() => void>();
const threadDetailSubscriptions = new Map<string, ThreadDetailSubscriptionEntry>();
const lastAppliedProjectionVersionByEnvironment = new Map<
  EnvironmentId,
  {
    readonly sequence: number;
    readonly updatedAt: string | null;
  }
>();
const serverTokenRefreshLoops = new Map<EnvironmentId, () => void>();

let activeService: EnvironmentServiceState | null = null;
let lastBrowserHiddenAt: number | null = null;
let lastBrowserResumeReconnectAt = Number.NEGATIVE_INFINITY;

// TODO(CLIENT-RUNTIME MIGRATION - DO NOT EXPAND THIS WEB-ONLY COPY):
// This file still owns web's legacy thread-detail subscription cache. Mobile
// uses createThreadDetailManager from @t3tools/client-runtime for the same
// retain/reconnect/evict lifecycle. When touching this logic, prefer migrating
// web to the shared manager or extracting the missing adapter layer instead of
// adding more behavior here.
//
// Thread detail subscription cache policy:
// - Active consumers keep a subscription retained via refCount.
// - Released subscriptions stay warm for a longer idle TTL to avoid churn
//   while moving around the UI.
// - Threads with active work or pending user action are sticky and are never
//   evicted while they remain non-idle.
// - Capacity eviction only targets idle cached subscriptions.
// - On reconnect only threads on screen and non-idle threads resubscribe at
//   once. Every other cached subscription drops its detail and stays deferred
//   until a view retains the thread again.
const THREAD_DETAIL_SUBSCRIPTION_IDLE_EVICTION_MS = 15 * 60 * 1000;
const MAX_CACHED_THREAD_DETAIL_SUBSCRIPTIONS = 32;
const BROWSER_RESUME_RECONNECT_COOLDOWN_MS = 2_000;
const NOOP = () => undefined;
function compareAppliedProjectionVersion(
  left: { readonly sequence: number; readonly updatedAt: string | null },
  right: { readonly sequence: number; readonly updatedAt: string | null },
): number {
  if (left.sequence !== right.sequence) {
    return left.sequence - right.sequence;
  }

  const leftUpdatedAt = left.updatedAt ?? "";
  const rightUpdatedAt = right.updatedAt ?? "";
  if (leftUpdatedAt === rightUpdatedAt) {
    return 0;
  }

  return leftUpdatedAt < rightUpdatedAt ? -1 : 1;
}

function toAppliedProjectionVersion(
  snapshot: Pick<ClientOrchestrationShellSnapshot, "snapshotSequence" | "updatedAt">,
): {
  readonly sequence: number;
  readonly updatedAt: string;
} {
  return {
    sequence: snapshot.snapshotSequence,
    updatedAt: snapshot.updatedAt,
  };
}

export function shouldApplyProjectionSnapshot(input: {
  readonly current: {
    readonly sequence: number;
    readonly updatedAt: string | null;
  } | null;
  readonly next: Pick<ClientOrchestrationShellSnapshot, "snapshotSequence" | "updatedAt">;
}): boolean {
  if (input.current === null) {
    return true;
  }

  return compareAppliedProjectionVersion(input.current, toAppliedProjectionVersion(input.next)) < 0;
}

export function shouldApplyProjectionEvent(input: {
  readonly current: {
    readonly sequence: number;
    readonly updatedAt: string | null;
  } | null;
  readonly sequence: number;
}): boolean {
  if (input.current === null) {
    return true;
  }

  return input.sequence > input.current.sequence;
}

function readLastAppliedProjectionVersion(environmentId: EnvironmentId): {
  readonly sequence: number;
  readonly updatedAt: string | null;
} | null {
  return lastAppliedProjectionVersionByEnvironment.get(environmentId) ?? null;
}

function markAppliedProjectionEvent(environmentId: EnvironmentId, sequence: number): void {
  const currentVersion = readLastAppliedProjectionVersion(environmentId);
  if (currentVersion !== null && sequence <= currentVersion.sequence) {
    return;
  }

  lastAppliedProjectionVersionByEnvironment.set(environmentId, {
    sequence,
    updatedAt: currentVersion?.updatedAt ?? null,
  });
}
function getThreadDetailSubscriptionKey(environmentId: EnvironmentId, threadId: ThreadId): string {
  return scopedThreadKey(scopeThreadRef(environmentId, threadId));
}

function clearThreadDetailSubscriptionEviction(
  entry: ThreadDetailSubscriptionEntry,
): ThreadDetailSubscriptionEntry {
  if (entry.evictionTimeoutId !== null) {
    clearTimeout(entry.evictionTimeoutId);
    entry.evictionTimeoutId = null;
  }
  return entry;
}

function isNonIdleThreadDetailSubscription(entry: ThreadDetailSubscriptionEntry): boolean {
  const threadRef = scopeThreadRef(entry.environmentId, entry.threadId);
  const state = useStore.getState();
  const sidebarThread = selectSidebarThreadSummaryByRef(state, threadRef);

  if (sidebarThread) {
    const orchestrationStatus = sidebarThread.session?.orchestrationStatus;
    if (
      orchestrationStatus &&
      orchestrationStatus !== "idle" &&
      orchestrationStatus !== "stopped"
    ) {
      return true;
    }

    if (sidebarThread.latestTurn?.state === "running") {
      return true;
    }
  }

  const thread = selectThreadByRef(state, threadRef);
  if (!thread) {
    return false;
  }

  const orchestrationStatus = thread.session?.orchestrationStatus;
  return (
    Boolean(
      orchestrationStatus && orchestrationStatus !== "idle" && orchestrationStatus !== "stopped",
    ) || thread.latestTurn?.state === "running"
  );
}

function shouldEvictThreadDetailSubscription(entry: ThreadDetailSubscriptionEntry): boolean {
  return entry.refCount === 0 && !isNonIdleThreadDetailSubscription(entry);
}

function shouldResubscribeThreadDetailOnReconnect(entry: ThreadDetailSubscriptionEntry): boolean {
  return entry.viewCount > 0 || isNonIdleThreadDetailSubscription(entry);
}

function deferThreadDetailSubscription(entry: ThreadDetailSubscriptionEntry): void {
  entry.unsubscribeConnectionListener?.();
  entry.unsubscribeConnectionListener = null;
  entry.unsubscribe();
  entry.unsubscribe = NOOP;
  entry.deferred = true;
  useStore.getState().clearThreadDetail(entry.threadId, entry.environmentId);
}

function attachThreadDetailSubscription(entry: ThreadDetailSubscriptionEntry): boolean {
  if (entry.unsubscribeConnectionListener !== null) {
    entry.unsubscribeConnectionListener();
    entry.unsubscribeConnectionListener = null;
  }
  if (entry.unsubscribe !== NOOP) {
    return true;
  }

  const connection = readEnvironmentConnection(entry.environmentId);
  if (!connection) {
    return false;
  }

  entry.deferred = false;
  entry.unsubscribe = connection.client.orchestration.subscribeThread(
    { threadId: entry.threadId },
    (item) => {
      if (item.kind === "rail") {
        useStore.getState().syncThreadRail(entry.environmentId, entry.threadId, item.update);
        return;
      }
      if (item.kind === "snapshot") {
        useStore
          .getState()
          .syncServerThreadDetail(
            item.snapshot.thread,
            entry.environmentId,
            item.snapshot.messageWindow,
            item.snapshot.activityWindow,
            item.snapshot.liveMessages,
          );
        return;
      }
      if (item.kind === "live") {
        useStore.getState().applyLiveMessageFrame(item.live, entry.environmentId);
        return;
      }
      applyEnvironmentThreadDetailEvent(item.event, entry.environmentId);
    },
    {
      onResubscribe: () => {
        if (!shouldResubscribeThreadDetailOnReconnect(entry)) {
          deferThreadDetailSubscription(entry);
        }
      },
    },
  );
  return true;
}

export function loadOlderThreadMessages(environmentId: EnvironmentId, threadId: ThreadId): void {
  const windowState = selectEnvironmentState(useStore.getState(), environmentId)
    .messageWindowByThreadId[threadId];
  if (
    !windowState ||
    !windowState.hasMoreBefore ||
    windowState.loadingOlder ||
    !windowState.oldestCursor
  ) {
    return;
  }

  const connection = readEnvironmentConnection(environmentId);
  if (!connection) {
    return;
  }

  const beforeCursor = windowState.oldestCursor;
  useStore.getState().beginLoadingOlder(threadId, environmentId);
  void connection.client.orchestration
    .fetchOlderThreadMessages({
      threadId,
      beforeCursor,
      limit: OLDER_MESSAGE_PAGE_SIZE,
    })
    .then((result) => {
      useStore.getState().prependOlderMessages(threadId, environmentId, {
        messages: result.messages.map((message) => mapMessage(environmentId, message)),
        hasMoreBefore: result.hasMoreBefore,
        oldestCursor: result.oldestCursor,
      });
    })
    .catch(() => {
      // On failure, clear loadingOlder (keep the same window) so the user can retry.
      useStore.getState().prependOlderMessages(threadId, environmentId, {
        messages: [],
        hasMoreBefore: windowState.hasMoreBefore,
        oldestCursor: beforeCursor,
      });
    });
}

export function loadOlderThreadActivities(environmentId: EnvironmentId, threadId: ThreadId): void {
  const windowState = selectEnvironmentState(useStore.getState(), environmentId)
    .activityWindowByThreadId[threadId];
  if (
    !windowState ||
    !windowState.hasMoreBefore ||
    windowState.loadingOlder ||
    !windowState.oldestCursor
  ) {
    return;
  }

  const connection = readEnvironmentConnection(environmentId);
  if (!connection) {
    return;
  }

  const beforeCursor = windowState.oldestCursor;
  useStore.getState().beginLoadingOlderActivities(threadId, environmentId);
  void connection.client.orchestration
    .fetchOlderThreadActivities({
      threadId,
      beforeCursor,
      limit: OLDER_ACTIVITY_PAGE_SIZE,
    })
    .then((result) => {
      useStore.getState().prependOlderActivities(threadId, environmentId, {
        activities: result.activities.map((activity) => ({ ...activity })),
        hasMoreBefore: result.hasMoreBefore,
        oldestCursor: result.oldestCursor,
      });
    })
    .catch(() => {
      useStore.getState().prependOlderActivities(threadId, environmentId, {
        activities: [],
        hasMoreBefore: windowState.hasMoreBefore,
        oldestCursor: beforeCursor,
      });
    });
}

function watchThreadDetailSubscriptionConnection(entry: ThreadDetailSubscriptionEntry): void {
  if (entry.unsubscribeConnectionListener !== null) {
    return;
  }

  entry.unsubscribeConnectionListener = subscribeEnvironmentConnections(() => {
    if (attachThreadDetailSubscription(entry)) {
      entry.lastAccessedAt = Date.now();
    }
  });
  attachThreadDetailSubscription(entry);
}

function disposeThreadDetailSubscriptionByKey(key: string): boolean {
  const entry = threadDetailSubscriptions.get(key);
  if (!entry) {
    return false;
  }

  clearThreadDetailSubscriptionEviction(entry);
  entry.unsubscribeConnectionListener?.();
  entry.unsubscribeConnectionListener = null;
  threadDetailSubscriptions.delete(key);
  entry.unsubscribe();
  entry.unsubscribe = NOOP;
  if (shouldEvictThreadDetailSubscription(entry)) {
    useStore.getState().clearThreadDetail(entry.threadId, entry.environmentId);
  }
  return true;
}

function detachThreadDetailSubscriptionsForEnvironment(environmentId: EnvironmentId): void {
  for (const entry of threadDetailSubscriptions.values()) {
    if (entry.environmentId !== environmentId) {
      continue;
    }
    if (!shouldResubscribeThreadDetailOnReconnect(entry)) {
      deferThreadDetailSubscription(entry);
      continue;
    }
    entry.unsubscribe();
    entry.unsubscribe = NOOP;
    watchThreadDetailSubscriptionConnection(entry);
  }
}

function attachThreadDetailSubscriptionsForEnvironment(environmentId: EnvironmentId): void {
  for (const entry of threadDetailSubscriptions.values()) {
    if (entry.environmentId === environmentId && !entry.deferred) {
      attachThreadDetailSubscription(entry);
    }
  }
}

function reconcileThreadDetailSubscriptionsForEnvironment(
  environmentId: EnvironmentId,
  threadIds: ReadonlyArray<ThreadId>,
): void {
  const activeThreadIds = new Set(threadIds);
  for (const [key, entry] of threadDetailSubscriptions) {
    if (entry.environmentId === environmentId && !activeThreadIds.has(entry.threadId)) {
      disposeThreadDetailSubscriptionByKey(key);
    }
  }
}

function scheduleThreadDetailSubscriptionEviction(entry: ThreadDetailSubscriptionEntry): void {
  clearThreadDetailSubscriptionEviction(entry);
  if (!shouldEvictThreadDetailSubscription(entry)) {
    return;
  }

  entry.evictionTimeoutId = setTimeout(() => {
    const currentEntry = threadDetailSubscriptions.get(
      getThreadDetailSubscriptionKey(entry.environmentId, entry.threadId),
    );
    if (!currentEntry) {
      return;
    }

    currentEntry.evictionTimeoutId = null;
    if (!shouldEvictThreadDetailSubscription(currentEntry)) {
      return;
    }
    disposeThreadDetailSubscriptionByKey(
      getThreadDetailSubscriptionKey(entry.environmentId, entry.threadId),
    );
  }, THREAD_DETAIL_SUBSCRIPTION_IDLE_EVICTION_MS);
}

function evictIdleThreadDetailSubscriptionsToCapacity(): void {
  if (threadDetailSubscriptions.size <= MAX_CACHED_THREAD_DETAIL_SUBSCRIPTIONS) {
    return;
  }

  const idleEntries = [...threadDetailSubscriptions.entries()]
    .filter(([, entry]) => shouldEvictThreadDetailSubscription(entry))
    .toSorted(([, left], [, right]) => left.lastAccessedAt - right.lastAccessedAt);

  for (const [key] of idleEntries) {
    if (threadDetailSubscriptions.size <= MAX_CACHED_THREAD_DETAIL_SUBSCRIPTIONS) {
      return;
    }
    disposeThreadDetailSubscriptionByKey(key);
  }
}

function reconcileThreadDetailSubscriptionEvictionState(
  entry: ThreadDetailSubscriptionEntry,
): void {
  clearThreadDetailSubscriptionEviction(entry);
  if (!shouldEvictThreadDetailSubscription(entry)) {
    return;
  }

  scheduleThreadDetailSubscriptionEviction(entry);
}

function reconcileThreadDetailSubscriptionEvictionForThread(
  environmentId: EnvironmentId,
  threadId: ThreadId,
): void {
  const entry = threadDetailSubscriptions.get(
    getThreadDetailSubscriptionKey(environmentId, threadId),
  );
  if (!entry) {
    return;
  }

  reconcileThreadDetailSubscriptionEvictionState(entry);
}

function reconcileThreadDetailSubscriptionEvictionForEnvironment(
  environmentId: EnvironmentId,
): void {
  for (const entry of threadDetailSubscriptions.values()) {
    if (entry.environmentId === environmentId) {
      reconcileThreadDetailSubscriptionEvictionState(entry);
    }
  }
  evictIdleThreadDetailSubscriptionsToCapacity();
}

function retainThreadDetailSubscriptionEntry(
  environmentId: EnvironmentId,
  threadId: ThreadId,
  purpose: ThreadDetailRetainPurpose,
): () => void {
  const key = getThreadDetailSubscriptionKey(environmentId, threadId);
  const entry: ThreadDetailSubscriptionEntry = threadDetailSubscriptions.get(key) ?? {
    environmentId,
    threadId,
    unsubscribe: NOOP,
    unsubscribeConnectionListener: null,
    refCount: 0,
    viewCount: 0,
    deferred: false,
    lastAccessedAt: Date.now(),
    evictionTimeoutId: null,
  };
  threadDetailSubscriptions.set(key, entry);
  clearThreadDetailSubscriptionEviction(entry);
  entry.refCount += 1;
  if (purpose === "view") {
    entry.viewCount += 1;
  }
  entry.lastAccessedAt = Date.now();
  if ((purpose === "view" || !entry.deferred) && !attachThreadDetailSubscription(entry)) {
    watchThreadDetailSubscriptionConnection(entry);
  }
  evictIdleThreadDetailSubscriptionsToCapacity();

  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    entry.refCount = Math.max(0, entry.refCount - 1);
    if (purpose === "view") {
      entry.viewCount = Math.max(0, entry.viewCount - 1);
    }
    entry.lastAccessedAt = Date.now();
    if (entry.refCount === 0) {
      reconcileThreadDetailSubscriptionEvictionState(entry);
      evictIdleThreadDetailSubscriptionsToCapacity();
    }
  };
}

export function retainThreadDetailSubscription(
  environmentId: EnvironmentId,
  threadId: ThreadId,
): () => void {
  return retainThreadDetailSubscriptionEntry(environmentId, threadId, "view");
}

export function prewarmThreadDetailSubscription(
  environmentId: EnvironmentId,
  threadId: ThreadId,
): () => void {
  return retainThreadDetailSubscriptionEntry(environmentId, threadId, "prewarm");
}

function emitEnvironmentConnectionRegistryChange() {
  for (const listener of environmentConnectionListeners) {
    listener();
  }
}

export function coalesceOrchestrationUiEvents(
  events: ReadonlyArray<ClientOrchestrationEvent>,
): ClientOrchestrationEvent[] {
  if (events.length < 2) {
    return [...events];
  }

  const coalesced: ClientOrchestrationEvent[] = [];
  for (const event of events) {
    const previous = coalesced.at(-1);
    if (
      previous?.type === "thread.message-sent" &&
      event.type === "thread.message-sent" &&
      previous.payload.threadId === event.payload.threadId &&
      previous.payload.messageId === event.payload.messageId
    ) {
      coalesced[coalesced.length - 1] = {
        ...event,
        payload: {
          ...event.payload,
          attachments: event.payload.attachments ?? previous.payload.attachments,
          createdAt: previous.payload.createdAt,
          // Always concatenate deltas for the same message. Earlier code branched on
          // `!streaming && text.length > 0`, which let a trailing `streaming=false` event
          // hide the accumulated stream-delta text from the reducer's append path.
          text: previous.payload.text + event.payload.text,
        },
      };
      continue;
    }

    coalesced.push(event);
  }

  return coalesced;
}

function syncProjectUiFromStore() {
  const projects = selectProjectsAcrossEnvironments(useStore.getState());
  useUiStateStore.getState().syncProjects(
    projects.map((project) => ({
      key: derivePhysicalProjectKey(project),
      logicalKey: deriveLogicalProjectKey(project),
      cwd: project.cwd,
    })),
  );
}

function syncThreadUiFromStore() {
  const threads = selectThreadsAcrossEnvironments(useStore.getState());
  useUiStateStore.getState().syncThreads(
    threads.map((thread) => ({
      key: scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
      seedVisitedAt: thread.updatedAt ?? thread.createdAt,
    })),
  );
  markPromotedDraftThreadsByRef(
    threads.map((thread) => scopeThreadRef(thread.environmentId, thread.id)),
  );
}

function reconcileSnapshotDerivedState() {
  syncProjectUiFromStore();
  syncThreadUiFromStore();
}

function applyRecoveredEventBatch(
  events: ReadonlyArray<ClientOrchestrationEvent>,
  environmentId: EnvironmentId,
) {
  if (events.length === 0) {
    return;
  }

  const batchEffects = deriveOrchestrationBatchEffects(events);
  const uiEvents = coalesceOrchestrationUiEvents(events);
  const needsProjectUiSync = events.some(
    (event) =>
      event.type === "project.created" ||
      event.type === "project.meta-updated" ||
      event.type === "project.deleted",
  );

  useStore.getState().applyOrchestrationEvents(uiEvents, environmentId);
  if (needsProjectUiSync) {
    const projects = selectProjectsAcrossEnvironments(useStore.getState());
    useUiStateStore.getState().syncProjects(
      projects.map((project) => ({
        key: derivePhysicalProjectKey(project),
        logicalKey: deriveLogicalProjectKey(project),
        cwd: project.cwd,
      })),
    );
  }

  const needsThreadUiSync = events.some(
    (event) => event.type === "thread.created" || event.type === "thread.deleted",
  );
  if (needsThreadUiSync) {
    const threads = selectThreadsAcrossEnvironments(useStore.getState());
    useUiStateStore.getState().syncThreads(
      threads.map((thread) => ({
        key: scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
        seedVisitedAt: thread.updatedAt ?? thread.createdAt,
      })),
    );
  }

  const draftStore = useComposerDraftStore.getState();
  for (const threadId of batchEffects.promoteDraftThreadIds) {
    markPromotedDraftThreadByRef(scopeThreadRef(environmentId, threadId));
  }
  for (const threadId of batchEffects.clearDeletedThreadIds) {
    draftStore.clearDraftThread(scopeThreadRef(environmentId, threadId));
    useUiStateStore
      .getState()
      .clearThreadUi(scopedThreadKey(scopeThreadRef(environmentId, threadId)));
  }
  for (const event of events) {
    if (event.type === "project.deleted") {
      draftStore.clearProjectDraftThreadId(scopeProjectRef(environmentId, event.payload.projectId));
    }
  }
  reconcileThreadDetailSubscriptionEvictionForEnvironment(environmentId);
}

export function applyEnvironmentThreadDetailEvent(
  event: ClientOrchestrationEvent,
  environmentId: EnvironmentId,
) {
  applyRecoveredEventBatch([event], environmentId);
}

function applyShellEvent(event: ClientOrchestrationShellStreamEvent, environmentId: EnvironmentId) {
  if (
    !shouldApplyProjectionEvent({
      current: readLastAppliedProjectionVersion(environmentId),
      sequence: event.sequence,
    })
  ) {
    return;
  }

  const threadId =
    event.kind === "thread-upserted"
      ? event.thread.id
      : event.kind === "thread-removed"
        ? event.threadId
        : null;
  const threadRef = threadId ? scopeThreadRef(environmentId, threadId) : null;
  const previousThread = threadRef ? selectThreadByRef(useStore.getState(), threadRef) : undefined;

  useStore.getState().applyShellEvent(event, environmentId);
  markAppliedProjectionEvent(environmentId, event.sequence);

  switch (event.kind) {
    case "project-upserted":
    case "project-removed":
      syncProjectUiFromStore();
      return;
    case "thread-upserted":
      syncThreadUiFromStore();
      if (!previousThread && threadRef) {
        markPromotedDraftThreadByRef(threadRef);
      }
      reconcileThreadDetailSubscriptionEvictionForThread(environmentId, event.thread.id);
      evictIdleThreadDetailSubscriptionsToCapacity();
      return;
    case "thread-removed":
      if (threadRef) {
        disposeThreadDetailSubscriptionByKey(scopedThreadKey(threadRef));
        useComposerDraftStore.getState().clearDraftThread(threadRef);
        useUiStateStore.getState().clearThreadUi(scopedThreadKey(threadRef));
      }
      syncThreadUiFromStore();
      return;
  }
}

function createEnvironmentConnectionHandlers() {
  return {
    applyShellEvent,
    syncShellSnapshot: (
      snapshot: ClientOrchestrationShellSnapshot,
      environmentId: EnvironmentId,
    ) => {
      useStore.getState().syncServerShellSnapshot(snapshot, environmentId);
      lastAppliedProjectionVersionByEnvironment.set(
        environmentId,
        toAppliedProjectionVersion(snapshot),
      );
      reconcileThreadDetailSubscriptionsForEnvironment(
        environmentId,
        snapshot.threads.map((thread) => thread.id),
      );
      reconcileThreadDetailSubscriptionEvictionForEnvironment(environmentId);
      reconcileSnapshotDerivedState();
    },
  };
}

function createWsRpcClient(transport: WsTransport): WsRpcClient {
  return createBaseWsRpcClient(transport, {
    beforeReconnect: () => resetWsReconnectBackoff(),
  });
}

/**
 * Mints a fresh single-use WebSocket ticket and builds the dial URL from it.
 * Passed to `WsTransport` as a URL *provider* (not a cached string) because a
 * ticket is consumed on first use — `WsTransport` invokes this again for
 * every connection attempt, including reconnects, so each attempt gets its
 * own ticket rather than replaying an already-spent one.
 */
async function resolvePrimaryWebSocketConnectionUrl(wsBaseUrl: string): Promise<string> {
  const issued = await issueWebSocketTicket();
  const url = new URL(wsBaseUrl);
  url.searchParams.set("wsTicket", issued.ticket);
  return url.toString();
}

function createPrimaryEnvironmentClient(knownEnvironment: PrimaryKnownEnvironment) {
  const wsBaseUrl = knownEnvironment.target.wsBaseUrl;
  const connectionLabel = knownEnvironment.label;

  return createWsRpcClient(
    new WsTransport(() => resolvePrimaryWebSocketConnectionUrl(wsBaseUrl), {
      getConnectionLabel: () => connectionLabel,
      getVersionMismatchHint: () =>
        resolveServerConfigVersionMismatch(getServerConfig())?.hint ?? null,
    }),
  );
}

/**
 * Refreshes on the timer, browser visibility and network return. Replacement
 * tokens travel over the open socket because reconnecting tears down every
 * live subscription on the connection. A failed refresh waits for the next
 * tick because tearing the connection down defeats offline grace.
 */
function startServerTokenRefreshLoop(client: WsRpcClient): () => void {
  const refresh = () => {
    void refreshServerToken(client);
  };
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") refresh();
  };
  const intervalId = setInterval(refresh, SERVER_TOKEN_REFRESH_INTERVAL_SECONDS * 1_000);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("online", refresh);
  return () => {
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("online", refresh);
  };
}

async function refreshServerToken(client: WsRpcClient): Promise<void> {
  try {
    const token = await acquireServerToken();
    if (token === null) {
      return;
    }
    attachServerToken(token);
    await client.server.refreshAuthToken({ token });
  } catch (error) {
    console.warn("Server token refresh failed; retrying next cycle", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

function registerConnection(connection: EnvironmentConnection): EnvironmentConnection {
  const existing = environmentConnections.get(connection.environmentId);
  if (existing && existing !== connection) {
    throw new Error(`Environment ${connection.environmentId} already has an active connection.`);
  }
  environmentConnections.set(connection.environmentId, connection);
  serverTokenRefreshLoops.get(connection.environmentId)?.();
  serverTokenRefreshLoops.set(
    connection.environmentId,
    startServerTokenRefreshLoop(connection.client),
  );
  attachThreadDetailSubscriptionsForEnvironment(connection.environmentId);
  emitEnvironmentConnectionRegistryChange();
  return connection;
}

async function removeConnection(environmentId: EnvironmentId): Promise<boolean> {
  const connection = environmentConnections.get(environmentId);
  if (!connection) {
    return false;
  }

  lastAppliedProjectionVersionByEnvironment.delete(environmentId);
  environmentConnections.delete(environmentId);
  serverTokenRefreshLoops.get(environmentId)?.();
  serverTokenRefreshLoops.delete(environmentId);
  emitEnvironmentConnectionRegistryChange();
  detachThreadDetailSubscriptionsForEnvironment(environmentId);
  await connection.dispose();
  return true;
}

function createPrimaryEnvironmentConnection(): EnvironmentConnection {
  const knownEnvironment = getPrimaryKnownEnvironment();
  if (!knownEnvironment?.environmentId) {
    throw new Error(t("environments/unableToResolveThe_kzc0mw"));
  }

  const existing = environmentConnections.get(knownEnvironment.environmentId);
  if (existing) {
    return existing;
  }

  return registerConnection(
    createEnvironmentConnection({
      knownEnvironment,
      client: createPrimaryEnvironmentClient(knownEnvironment),
      fetchShellSnapshot: () =>
        fetchShellSnapshot({
          url: resolvePrimaryEnvironmentHttpUrl("/api/orchestration/shell"),
          fetch: primaryEnvironmentFetch,
          // Cross-origin cookies make the browser reject loopback's wildcard CORS response.
          requestInit: { ...primaryEnvironmentRequestInit, credentials: "same-origin" },
        }),
      ...createEnvironmentConnectionHandlers(),
    }),
  );
}

function maybeCreatePrimaryEnvironmentConnection(): EnvironmentConnection | null {
  return getPrimaryKnownEnvironment()?.environmentId ? createPrimaryEnvironmentConnection() : null;
}

function stopActiveService() {
  activeService?.stop();
  activeService = null;
}

function reconnectEnvironmentConnectionsAfterBrowserResume(reason: string): void {
  const now = Date.now();
  if (now - lastBrowserResumeReconnectAt < BROWSER_RESUME_RECONNECT_COOLDOWN_MS) {
    return;
  }

  for (const connection of environmentConnections.values()) {
    if (connection.client.isHeartbeatFresh()) {
      continue;
    }
    lastBrowserResumeReconnectAt = now;
    void connection.reconnect().catch((error) => {
      console.warn("Environment reconnect after browser resume failed", {
        environmentId: connection.environmentId,
        reason,
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }
}

function subscribeBrowserResumeReconnects(): () => void {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return NOOP;
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      lastBrowserHiddenAt = Date.now();
      return;
    }
    if (document.visibilityState === "visible" && lastBrowserHiddenAt !== null) {
      lastBrowserHiddenAt = null;
      reconnectEnvironmentConnectionsAfterBrowserResume("visibilitychange");
    }
  };

  const handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted || lastBrowserHiddenAt !== null) {
      lastBrowserHiddenAt = null;
      reconnectEnvironmentConnectionsAfterBrowserResume("pageshow");
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pageshow", handlePageShow);
  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pageshow", handlePageShow);
  };
}

export function subscribeEnvironmentConnections(listener: () => void): () => void {
  environmentConnectionListeners.add(listener);
  return () => {
    environmentConnectionListeners.delete(listener);
  };
}

export function listEnvironmentConnections(): ReadonlyArray<EnvironmentConnection> {
  return [...environmentConnections.values()];
}

export function readEnvironmentConnection(
  environmentId: EnvironmentId,
): EnvironmentConnection | null {
  return environmentConnections.get(environmentId) ?? null;
}

export function requireEnvironmentConnection(environmentId: EnvironmentId): EnvironmentConnection {
  const connection = readEnvironmentConnection(environmentId);
  if (!connection) {
    throw new Error(`No websocket client registered for environment ${environmentId}.`);
  }
  return connection;
}

export function getPrimaryEnvironmentConnection(): EnvironmentConnection {
  return createPrimaryEnvironmentConnection();
}

export async function ensureEnvironmentConnectionBootstrapped(
  environmentId: EnvironmentId,
): Promise<void> {
  await environmentConnections.get(environmentId)?.ensureBootstrapped();
}

export function startEnvironmentConnectionService(queryClient: QueryClient): () => void {
  if (activeService?.queryClient === queryClient) {
    activeService.refCount += 1;
    return () => {
      if (!activeService || activeService.queryClient !== queryClient) {
        return;
      }
      activeService.refCount -= 1;
      if (activeService.refCount === 0) {
        stopActiveService();
      }
    };
  }

  stopActiveService();
  maybeCreatePrimaryEnvironmentConnection();

  const unsubscribeBrowserResumeReconnects = subscribeBrowserResumeReconnects();

  activeService = {
    queryClient,
    refCount: 1,
    stop: () => {
      unsubscribeBrowserResumeReconnects();
    },
  };

  return () => {
    if (!activeService || activeService.queryClient !== queryClient) {
      return;
    }
    activeService.refCount -= 1;
    if (activeService.refCount === 0) {
      stopActiveService();
    }
  };
}

export async function resetEnvironmentServiceForTests(): Promise<void> {
  stopActiveService();
  lastBrowserHiddenAt = null;
  lastBrowserResumeReconnectAt = Number.NEGATIVE_INFINITY;
  lastAppliedProjectionVersionByEnvironment.clear();
  for (const key of Array.from(threadDetailSubscriptions.keys())) {
    disposeThreadDetailSubscriptionByKey(key);
  }
  for (const stop of serverTokenRefreshLoops.values()) {
    stop();
  }
  serverTokenRefreshLoops.clear();
  await Promise.all(
    [...environmentConnections.keys()].map((environmentId) => removeConnection(environmentId)),
  );
}
